import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStorePos } from "./useStorePos";
import PosPopupPreciosEditar from "./PosPopupPreciosEditar";
import { formatearMoneda } from "../../config/moneda";
import { PERMISOS, puedeAcceder } from "../../servicios/sesionSeguridad";

const normalizar = (value) => String(value || "").trim().toLowerCase();

const PosPopupPrecios = () => {
  const { t } = useTranslation();
  const puedeModificar = puedeAcceder(PERMISOS.PRECIOS_MODIFICAR);
  const ListaPreciosEditable = useStorePos((s) => s.ListaPreciosEditable);
  const CargandoListaPrecios = useStorePos((s) => s.CargandoListaPrecios);
  const cargarListaPreciosEditable = useStorePos((s) => s.cargarListaPreciosEditable);
  const [mostrarEditor, setMostrarEditor] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [filtro, setFiltro] = useState("todos");

  useEffect(() => {
    cargarListaPreciosEditable();
  }, [cargarListaPreciosEditable]);

  const resumen = useMemo(() => {
    const total = ListaPreciosEditable.length;
    const conPrecio = ListaPreciosEditable.filter((p) => Number(p.nPrecio) > 0).length;
    const sinPrecio = total - conPrecio;
    const suma = ListaPreciosEditable.reduce((acc, p) => acc + (Number(p.nPrecio) || 0), 0);

    return {
      total,
      conPrecio,
      sinPrecio,
      promedio: conPrecio > 0 ? suma / conPrecio : 0,
    };
  }, [ListaPreciosEditable]);

  const productosFiltrados = useMemo(() => {
    const texto = normalizar(busqueda);

    return ListaPreciosEditable.filter((p) => {
      const precio = Number(p.nPrecio) || 0;
      const coincideFiltro =
        filtro === "todos" ||
        (filtro === "conPrecio" && precio > 0) ||
        (filtro === "sinPrecio" && precio <= 0);
      const coincideTexto =
        !texto ||
        normalizar(p.sDescripcion).includes(texto) ||
        normalizar(p.sCodigo).includes(texto);

      return coincideFiltro && coincideTexto;
    });
  }, [ListaPreciosEditable, busqueda, filtro]);

  return (
    <section className="pos-pendientes pos-catalogo-page pos-precios-page" aria-labelledby="pos-precios-titulo">
      <header className="pos-pendientes-header pos-catalogo-header">
        <div className="pos-catalogo-heading pos-section-title">
          <span className="pos-catalogo-heading-icon pos-section-icon" aria-hidden="true">
            <i className="pi pi-tags" />
          </span>
          <div>
            <span className="pos-catalogo-kicker">{t("pos.title")}</span>
            <h2 id="pos-precios-titulo" className="ing-subtitulo">{t("pos.priceList")}</h2>
            <p>{t("products.priceListHelp")}</p>
          </div>
        </div>
        {puedeModificar && <button
          type="button"
          className="pos-btn-principal pos-btn-agregar-producto"
          onClick={() => setMostrarEditor(true)}
        >
          <i className="pi pi-pencil" /> {t("products.modifyPrices")}
        </button>}
      </header>

      <div className="pos-precios-modal">
        <div className="pos-precios-topbar">
          <span className="pos-precios-note">
            <i className="pi pi-info-circle" />
            {t("products.pricesWithoutVat")}
          </span>
          <span className="pos-precios-count">{t("pos.productCount", { count: productosFiltrados.length })}</span>
        </div>

        <div className="pos-precios-resumen" aria-label={t("pos.saleSummary")}>
          <div>
            <span>{t("products.products")}</span>
            <strong>{resumen.total}</strong>
          </div>
          <div>
            <span>{t("products.withPrice")}</span>
            <strong>{resumen.conPrecio}</strong>
          </div>
          <div>
            <span>{t("products.withoutPrice")}</span>
            <strong>{resumen.sinPrecio}</strong>
          </div>
          <div>
            <span>{t("products.average")}</span>
            <strong>{formatearMoneda(resumen.promedio)}</strong>
          </div>
        </div>

        <div className="pos-precios-toolbar">
          <label className="pos-precios-search">
            <i className="pi pi-search" />
            <span className="pos-sr-only">{t("products.searchByCodeOrDescription")}</span>
            <input
              type="search"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder={t("products.searchByCodeOrDescription")}
            />
          </label>

          <div className="pos-precios-filtros" role="group" aria-label={t("products.priceFilters")}>
            <button
              type="button"
              className={filtro === "todos" ? "activo" : ""}
              aria-pressed={filtro === "todos"}
              onClick={() => setFiltro("todos")}
            >
              {t("common.all")}
            </button>
            <button
              type="button"
              className={filtro === "conPrecio" ? "activo" : ""}
              aria-pressed={filtro === "conPrecio"}
              onClick={() => setFiltro("conPrecio")}
            >
              {t("products.withPrice")}
            </button>
            <button
              type="button"
              className={filtro === "sinPrecio" ? "activo" : ""}
              aria-pressed={filtro === "sinPrecio"}
              onClick={() => setFiltro("sinPrecio")}
            >
              {t("products.withoutPrice")}
            </button>
          </div>
        </div>

        <div className="pos-precios-tabla-wrap">
          {CargandoListaPrecios ? (
            <div className="pos-precios-state">
              <i className="pi pi-spin pi-spinner" />
              <strong>{t("products.loadingList")}</strong>
            </div>
          ) : productosFiltrados.length === 0 ? (
            <div className="pos-precios-state">
              <i className="pi pi-search" />
              <strong>{t("products.noProductsToShow")}</strong>
              <span>{t("products.reviewSearchOrFilter")}</span>
            </div>
          ) : (
            <table className="pos-precios-tabla pos-precios-consulta-tabla">
              <thead>
                <tr>
                  <th>{t("pos.product")}</th>
                  <th className="pos-precios-col-precio">{t("products.priceWithoutVat")}</th>
                  <th className="pos-precios-col-estado">{t("common.status")}</th>
                </tr>
              </thead>
              <tbody>
                {productosFiltrados.map((producto) => {
                  const conPrecio = Number(producto.nPrecio) > 0;
                  return (
                    <tr key={producto.Id} className={conPrecio ? "" : "sin-precio"}>
                      <td data-label={t("pos.product")}>
                        <div className="pos-precios-producto-cell">
                          <strong>{producto.sDescripcion}</strong>
                          {producto.sCodigo && <span>{t("products.codeLabel", { code: producto.sCodigo })}</span>}
                        </div>
                      </td>
                      <td data-label={t("products.priceWithoutVat")} className="pos-precios-col-precio">
                        <strong>{formatearMoneda(producto.nPrecio)}</strong>
                      </td>
                      <td data-label={t("common.status")} className="pos-precios-col-estado">
                        <span className={`pos-precios-badge ${conPrecio ? "vigente" : "sin-precio"}`}>
                          {conPrecio ? t("products.current") : t("products.withoutPrice")}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {puedeModificar && <PosPopupPreciosEditar
        visible={mostrarEditor}
        onHide={() => setMostrarEditor(false)}
      />}
    </section>
  );
};

export default PosPopupPrecios;
